// GrammarGrid (3 × 2 verb grid) and DeclensionGrid (8 × 2 case grid).
// Every cell is a button; arrow keys move between cells. Two numbers only — there is no dual column.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Case, Num, Person } from '@/prakrit/lib/grammar/types'

import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { CASE_LABEL, KARAK, NUMBER_LABEL, PERSON_LABEL, SUBJECT } from '@/prakrit/lib/grammar/labels'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'

export interface GridCell {
    core: string[]
    alt: string[]
    /** Review state of the core form(s). */
    review?: string
}

const moveFocus = (event: KeyboardEvent<HTMLElement>, rows: number, cols: number) => {
    const target = event.target as HTMLElement
    const row = Number(target.dataset.row)
    const col = Number(target.dataset.col)

    if (Number.isNaN(row) || Number.isNaN(col)) return

    let r = row
    let c = col

    if (event.key === 'ArrowDown') r = Math.min(rows - 1, row + 1)
    else if (event.key === 'ArrowUp') r = Math.max(0, row - 1)
    else if (event.key === 'ArrowRight') c = Math.min(cols - 1, col + 1)
    else if (event.key === 'ArrowLeft') c = Math.max(0, col - 1)
    else if (event.key === 'Home') c = 0
    else if (event.key === 'End') c = cols - 1
    else return

    event.preventDefault()

    const grid = target.closest('[data-grid]')
    const next = grid?.querySelector<HTMLElement>(`[data-row="${r}"][data-col="${c}"]`)

    next?.focus()
}

interface CellButtonProps {
    cell: GridCell
    row: number
    col: number
    label: string
    selected: boolean
    highlighted: boolean
    showAlt: boolean
    showReview: boolean
    tabbable: boolean
    onClick?: () => void
    render?: ReactNode
}

const CellButton: FC<CellButtonProps> = ({
    cell,
    row,
    col,
    label,
    selected,
    highlighted,
    showAlt,
    showReview,
    tabbable,
    onClick,
    render
}): ReactNode => (
    <button
        type='button'
        data-row={row}
        data-col={col}
        tabIndex={tabbable ? 0 : -1}
        aria-label={`${label}: ${cell.core.join(', ')}`}
        aria-pressed={selected}
        onClick={onClick}
        className={cn(
            'group relative flex min-h-tap w-full flex-col items-start justify-center gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
            'border-transparent bg-secondary/70 hover:border-saffron/60 hover:bg-secondary',
            highlighted && 'border-saffron bg-saffron hover:bg-saffron/90',
            selected && 'border-prakrit ring-2 ring-prakrit/25'
        )}
    >
        {render ?? (
            <>
                <span lang='pra' className='pra pra-sm flex flex-wrap gap-x-2'>
                    {cell.core.length > 0 ? cell.core.join(' / ') : <span className='font-sans text-sm font-normal text-muted-foreground'>—</span>}
                </span>
                {showAlt && cell.alt.length > 0 && (
                    <span className='flex flex-wrap items-center gap-1'>
                        <span lang='pra' className='font-prakrit text-sm text-terracotta'>{cell.alt.join(', ')}</span>
                        <StatusBadge kind='alt' withEnglish={false} />
                    </span>
                )}
                {showReview && cell.review && cell.review !== 'verified' && cell.review !== 'published' && (
                    <StatusBadge kind={reviewBadgeKind(cell.review)} withEnglish={false} className='absolute right-1.5 top-1.5' />
                )}
            </>
        )}
    </button>
)

interface GrammarGridProps {
    cells: Record<Person, Record<Num, GridCell>>
    caption?: ReactNode
    showAlt?: boolean
    showReview?: boolean
    showSubjects?: boolean
    highlight?: { person: Person, number: Num }[]
    selected?: { person: Person, number: Num } | null
    onSelect?: (person: Person, number: Num) => void
    renderCell?: (person: Person, number: Num, cell: GridCell) => ReactNode | undefined
    className?: string
}

export const GrammarGrid: FC<GrammarGridProps> = ({
    cells,
    caption,
    showAlt = false,
    showReview = false,
    showSubjects = true,
    highlight = [],
    selected = null,
    onSelect,
    renderCell,
    className
}): ReactNode => {
    const isHighlighted = (p: Person, n: Num) => highlight.some(h => h.person === p && h.number === n)

    return (
        <div data-grid='verb' role='grid' aria-label='पुरुष × वचन' onKeyDown={e => moveFocus(e, 3, 2)} className={cn('w-full', className)}>
            {caption && <div className='mb-3'>{caption}</div>}
            <div role='row' className='grid grid-cols-[minmax(6.5rem,0.8fr)_1fr_1fr] gap-2 pb-2'>
                <span />
                {NUMBERS.map(n => (
                    <span key={n} role='columnheader' className='flex flex-col px-1'>
                        <span lang='hi' className='hindi text-sm font-semibold'>{NUMBER_LABEL[n].hi}</span>
                        <span lang='en' data-ex='en' className='text-xs text-muted-foreground'>{NUMBER_LABEL[n].en}</span>
                    </span>
                ))}
            </div>
            <div className='flex flex-col gap-2'>
                {PERSONS.map((p, r) => (
                    <div key={p} role='row' className='grid grid-cols-[minmax(6.5rem,0.8fr)_1fr_1fr] items-stretch gap-2'>
                        <span role='rowheader' className='flex flex-col justify-center pr-1'>
                            <span lang='hi' className='hindi text-sm font-semibold leading-tight'>{PERSON_LABEL[p].hi}</span>
                            {showSubjects && (
                                <span lang='pra' className='font-prakrit text-sm text-prakrit'>{SUBJECT[p].eka} / {SUBJECT[p].bahu}</span>
                            )}
                        </span>
                        {NUMBERS.map((n, c) => (
                            <div key={n} role='gridcell'>
                                <CellButton
                                    cell={cells[p][n]}
                                    row={r}
                                    col={c}
                                    label={`${PERSON_LABEL[p].hi} ${NUMBER_LABEL[n].hi}`}
                                    selected={selected?.person === p && selected?.number === n}
                                    highlighted={isHighlighted(p, n)}
                                    showAlt={showAlt}
                                    showReview={showReview}
                                    tabbable={r === 0 && c === 0}
                                    onClick={onSelect ? () => onSelect(p, n) : undefined}
                                    render={renderCell?.(p, n, cells[p][n])}
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}

interface DeclensionGridProps {
    cells: Partial<Record<Case, Record<Num, GridCell>>>
    cases: Case[]
    showAlt?: boolean
    showReview?: boolean
    showKarak?: boolean
    selected?: { case: Case, number: Num } | null
    highlight?: { case: Case, number?: Num }[]
    onSelect?: (c: Case, number: Num) => void
    renderCell?: (c: Case, number: Num, cell: GridCell) => ReactNode | undefined
    className?: string
}

export const DeclensionGrid: FC<DeclensionGridProps> = ({
    cells,
    cases,
    showAlt = false,
    showReview = false,
    showKarak = true,
    selected = null,
    highlight = [],
    onSelect,
    renderCell,
    className
}): ReactNode => {
    const isHighlighted = (c: Case, n: Num) => highlight.some(h => h.case === c && (!h.number || h.number === n))

    return (
        <div data-grid='case' role='grid' aria-label='विभक्ति × वचन' onKeyDown={e => moveFocus(e, cases.length, 2)} className={cn('w-full', className)}>
            <div role='row' className='grid grid-cols-[minmax(7rem,0.9fr)_1fr_1fr] gap-2 pb-2'>
                <span />
                {NUMBERS.map(n => (
                    <span key={n} role='columnheader' className='flex flex-col px-1'>
                        <span lang='hi' className='hindi text-sm font-semibold'>{NUMBER_LABEL[n].hi}</span>
                        <span lang='en' data-ex='en' className='text-xs text-muted-foreground'>{NUMBER_LABEL[n].en}</span>
                    </span>
                ))}
            </div>
            <div className='flex flex-col gap-2'>
                {cases.map((c, r) => {
                    const row = cells[c]

                    if (!row) return null

                    return (
                        <div key={c} role='row' className='grid grid-cols-[minmax(7rem,0.9fr)_1fr_1fr] items-stretch gap-2'>
                            <span role='rowheader' className='flex flex-col justify-center pr-1'>
                                <span lang='hi' className='hindi text-sm font-semibold leading-tight'>{CASE_LABEL[c].hi}</span>
                                {showKarak && (
                                    <span className='text-xs leading-snug text-muted-foreground'>
                                        <span lang='hi' data-ex='hi' className='hindi'>{KARAK[c].hi.split(' (')[0]}</span>
                                        <span aria-hidden='true' className='ex-sep'> · </span>
                                        <span lang='en' data-ex='en'>{CASE_LABEL[c].en}</span>
                                    </span>
                                )}
                            </span>
                            {NUMBERS.map((n, col) => (
                                <div key={n} role='gridcell'>
                                    <CellButton
                                        cell={row[n]}
                                        row={r}
                                        col={col}
                                        label={`${CASE_LABEL[c].hi} ${NUMBER_LABEL[n].hi}`}
                                        selected={selected?.case === c && selected?.number === n}
                                        highlighted={isHighlighted(c, n)}
                                        showAlt={showAlt}
                                        showReview={showReview}
                                        tabbable={r === 0 && col === 0}
                                        onClick={onSelect ? () => onSelect(c, n) : undefined}
                                        render={renderCell?.(c, n, row[n])}
                                    />
                                </div>
                            ))}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
